import { nanoid } from 'nanoid'
import { resolveUploadName } from '@/user_files/drive/utils/resolve-upload-name'

export type UploadCandidate = { file: File; name: string; clientTmpId: string; folderId: string }
export type UnsupportedUploadFile = { file: File; clientTmpId: string; folderId: string }
export type ConflictingUploadFile = { file: File; clientTmpId: string; folderId: string; originalName: string; resolvedName: string }

export type CategorizedUploadFiles = {
  /** Files rejected outright by `isFileAccepted` (never counted against occupied names). */
  unsupported: UnsupportedUploadFile[]
  /** Files whose name collides with an existing file/queued upload/earlier file in this batch. */
  conflicts: ConflictingUploadFile[]
  /** Files with no name collision, ready to validate and queue for upload. */
  candidates: UploadCandidate[]
  /**
   * Names taken in this folder after the batch — existing/seed names plus every accepted
   * candidate's name — excluding conflict-resolved names. Seeds the conflict modal's per-folder
   * validation: a name should not be rejected there just because it was claimed as a *resolved*
   * fallback name for a different conflicting file in this same batch.
   */
  baseOccupiedNames: Set<string>
}

/**
 * Splits dropped files into unsupported / name-conflicting / ready-to-upload groups for one
 * target folder, given the names already taken there. Pure aside from `clientTmpId` generation —
 * no dispatch, no React state — so the batching and conflict-resolution rules can be unit tested
 * directly instead of only through the hook that dispatches their results.
 */
export function categorizeUploadFiles(params: {
  files: File[]
  folderId: string
  occupiedNamesSeed: ReadonlySet<string>
  isFileAccepted: (file: File) => boolean
}): CategorizedUploadFiles {
  const { files, folderId, occupiedNamesSeed, isFileAccepted } = params
  const occupiedNames = new Set(occupiedNamesSeed)
  const unsupported: UnsupportedUploadFile[] = []
  const conflicts: ConflictingUploadFile[] = []
  const candidates: UploadCandidate[] = []

  for (const file of files) {
    const clientTmpId = nanoid()

    if (!isFileAccepted(file)) {
      unsupported.push({ file, clientTmpId, folderId })
      continue
    }

    if (occupiedNames.has(file.name)) {
      const resolvedName = resolveUploadName(file.name, occupiedNames)
      // Add the resolved name so later files in this same batch don't reuse it too.
      occupiedNames.add(resolvedName)
      conflicts.push({ file, clientTmpId, folderId, originalName: file.name, resolvedName })
    } else {
      occupiedNames.add(file.name)
      candidates.push({ file, name: file.name, clientTmpId, folderId })
    }
  }

  const baseOccupiedNames = new Set(occupiedNames)
  for (const conflict of conflicts) baseOccupiedNames.delete(conflict.resolvedName)

  return { unsupported, conflicts, candidates, baseOccupiedNames }
}
